import React, { useEffect, useState, useCallback, useMemo } from 'react'; // Added useMemo
import { collection, getDocs, query, where, orderBy, Timestamp } from 'firebase/firestore'; // Removed doc, deleteDoc
import { db } from '../../firebase';
import { useLocation } from 'react-router-dom';
import { Calendar, momentLocalizer } from 'react-big-calendar';
import moment from 'moment';
import 'react-big-calendar/lib/css/react-big-calendar.css';
// Assuming a shared or similar CSS file exists or reusing admin's
import '../../pages/admin/LaporanPenggunaanAlat.css'; // Reusing admin CSS for now

// Setup the localizer by providing the moment Object
const localizer = momentLocalizer(moment);

// Helper to convert Firestore Timestamp to JS Date
const timestampToDate = (timestamp) => {
  return timestamp instanceof Timestamp ? timestamp.toDate() : null;
};

// Helper to format Firestore Timestamp to readable date and time string
const formatDateTime = (timestamp) => {
  if (timestamp instanceof Timestamp) {
    return timestamp.toDate().toLocaleString('id-ID', {
      year: 'numeric', month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit', hour12: false
    });
  }
  return 'N/A';
};

// Renamed component for Laboran
const LaboranJadwalPenggunaan = () => {
  const [alatList, setAlatList] = useState([]);
  const [selectedAlat, setSelectedAlat] = useState(null);
  const [scheduleEvents, setScheduleEvents] = useState([]);
  const [loadingAlat, setLoadingAlat] = useState(false);
  const [loadingSchedule, setLoadingSchedule] = useState(false);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState(''); // State for search term
  const location = useLocation();

  // Fetch list of instruments and glassware
  const fetchAlatList = useCallback(async () => {
    setLoadingAlat(true);
    setError('');
    try {
      const instrumenRef = collection(db, 'instrumen');
      const alatGelasRef = collection(db, 'alat_gelas');

      const [instrumenSnapshot, alatGelasSnapshot] = await Promise.all([
        getDocs(instrumenRef),
        getDocs(alatGelasRef)
      ]);

      const instrumenData = instrumenSnapshot.docs.map(doc => ({ id: doc.id, type: 'instrumen', ...doc.data() }));
      const alatGelasData = alatGelasSnapshot.docs.map(doc => ({ id: doc.id, type: 'alat_gelas', ...doc.data() }));

      const combinedList = [...instrumenData, ...alatGelasData].sort((a, b) => a.nama.localeCompare(b.nama));
      setAlatList(combinedList);

      if (location.state?.alatId && combinedList.length > 0) {
        const preselectedAlat = combinedList.find(item => item.id === location.state.alatId);
        if (preselectedAlat) {
          setTimeout(() => fetchScheduleForAlat(preselectedAlat), 0);
        } else {
          console.warn(`Preselected alat with ID ${location.state.alatId} not found.`);
        }
      }
    } catch (err) {
      console.error("Error fetching tools list:", err);
      setError('Gagal memuat daftar alat.');
    } finally {
      setLoadingAlat(false);
    }
  // Removed fetchScheduleForAlat from dependency array as it's defined below
  }, [location.state]);

  // Filtered list based on search term
  const filteredAlatList = useMemo(() => {
    if (!searchTerm) {
      return alatList;
    }
    return alatList.filter(item =>
      item.nama.toLowerCase().includes(searchTerm.toLowerCase())
      // Add other fields to search if needed, e.g., item.kode
    );
  }, [alatList, searchTerm]);

  // Fetch approved and returned requests for the selected alat
  const fetchScheduleForAlat = useCallback(async (alat) => {
    if (!alat) return;
    setLoadingSchedule(true);
    setError('');
    setSelectedAlat(alat);
    setScheduleEvents([]);
    try {
      const requestsRef = collection(db, 'permintaan_alat');
      const q = query(
        requestsRef,
        where('namaAlat', '==', alat.nama),
        where('status', 'in', ['approved', 'returned']),
        orderBy('tanggalPinjam', 'asc')
      );

      const querySnapshot = await getDocs(q);
      const events = querySnapshot.docs.map(docSnap => {
        const data = docSnap.data();
        const docId = docSnap.id;
        try {
          const start = timestampToDate(data.tanggalPinjam);
          const end = timestampToDate(data.tanggalKembali);

          if (!start || !end) {
            console.warn(`Invalid date for request ${docId}`);
            return null;
          }

          let adjustedEnd = new Date(end.getTime());
          if (moment(start).isSame(adjustedEnd, 'day')) {
              adjustedEnd.setHours(adjustedEnd.getHours() + 1);
          }

          return {
            id: docId,
            title: `${data.namaUser || 'N/A'} (${data.jumlah || 1})`,
            start: start,
            end: adjustedEnd,
            allDay: false,
            resource: data,
            status: data.status,
          };
        } catch (mapError) {
          console.error(`Error processing booking ${docId}:`, mapError);
          setError(`Error processing schedule data for ${alat.nama}.`);
          return null;
        }
      }).filter(event => event !== null);

      setScheduleEvents(events);
    } catch (err) {
      console.error(`Error fetching schedule for ${alat.nama}:`, err);
      setError(`Gagal memuat jadwal untuk ${alat.nama}.`);
    } finally {
      setLoadingSchedule(false);
    }
  }, []);

  // Fetch alat list on component mount
  useEffect(() => {
    fetchAlatList();
  }, [fetchAlatList]);

  return (
    // Using admin's container class name for consistency, adjust if needed
    <div className="laporan-alat-container">
      <h2>Jadwal Penggunaan Alat</h2> {/* Consistent Title */}

      {error && <div className="error-message">{error}</div>}

      {/* Alat List */}
      <div className="instrument-list-container">
        <h3>Pilih Alat:</h3>

        {/* Search Input */}
        <div className="search-container" style={{ marginBottom: '15px', maxWidth: '400px' }}>
          <input
            type="text"
            placeholder="Cari alat berdasarkan nama..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input" // Reuse class or create a new one
            style={{ width: '100%', padding: '8px 10px', borderRadius: '4px', border: '1px solid #ccc' }}
          />
        </div>

        {loadingAlat ? (
          <p>Memuat daftar alat...</p>
        ) : (
          <ul className="instrument-list">
            {/* Use filtered list */}
            {filteredAlatList.map((alat) => (
              <li key={alat.id}>
                <button
                  onClick={() => fetchScheduleForAlat(alat)}
                  className={selectedAlat?.id === alat.id ? 'selected' : ''}
                  disabled={loadingSchedule}
                >
                  {alat.nama} ({alat.type === 'instrumen' ? 'Instrumen' : 'Alat Gelas'})
                </button>
              </li>
            ))}
            {/* Update empty state message */}
            {filteredAlatList.length === 0 && !loadingAlat && (
               <li>{searchTerm ? 'Tidak ada alat yang cocok dengan pencarian.' : 'Tidak ada alat yang tersedia.'}</li>
            )}
          </ul>
        )}
      </div>

      {/* Calendar View */}
      {selectedAlat && (
        <div className="calendar-view-container">
           <h3>Jadwal untuk: {selectedAlat.nama}</h3>
           {loadingSchedule ? (
            <div className="loading-message">Memuat jadwal...</div>
          ) : (
            <Calendar
              localizer={localizer}
              events={scheduleEvents}
              startAccessor="start"
              endAccessor="end"
              style={{ height: 600 }}
              views={['month', 'week', 'day', 'agenda']}
              step={60}
              showMultiDayTimes
              eventPropGetter={(event) => {
                const style = {
                  backgroundColor: event.status === 'returned' ? '#808080' : '#3174ad',
                  borderRadius: '5px',
                  opacity: 0.8,
                  color: 'white',
                  border: '0px',
                  display: 'block'
                };
                return { style };
              }}
              messages={{ // Consistent messages
                today: 'Hari Ini',
                previous: 'Sebelumnya',
                next: 'Berikutnya',
                month: 'Bulan',
                week: 'Minggu',
                day: 'Hari',
                agenda: 'Agenda',
                date: 'Tanggal',
                time: 'Waktu',
                event: 'Acara',
                noEventsInRange: 'Tidak ada jadwal penggunaan dalam rentang ini.',
              }}
              // Removed onSelectEvent for Laboran view
            />
          )}
           {scheduleEvents.length === 0 && !loadingSchedule && (
             <p style={{ textAlign: 'center', marginTop: '20px' }}>
               Tidak ada jadwal penggunaan yang disetujui untuk alat ini.
             </p>
           )}
        </div>
      )}
    </div>
  );
};

export default LaboranJadwalPenggunaan; // Export renamed component
